interface RedirectConfiguration {
	authOrigin?: string;
	adminOrigin?: string;
}

type RedirectResult =
	| { kind: 'redirect'; url: string }
	| { kind: 'notice'; reason: 'invite' | 'not-found'; homeUrl: string };

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function validateOrigin(value: string, currentOrigin: string) {
	const url = new URL(value);
	const local = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
	if (
		(url.protocol !== 'https:' && !(local && url.protocol === 'http:')) ||
		url.username ||
		url.password ||
		url.pathname !== '/' ||
		url.search ||
		url.hash ||
		url.origin === currentOrigin
	)
		throw new Error('Invalid legacy redirect origin');
	return url.origin;
}

export function getLegacyRedirect(url: URL, configuration: RedirectConfiguration): RedirectResult | null {
	if (!configuration.authOrigin?.trim()) return null;
	const authOrigin = validateOrigin(configuration.authOrigin, url.origin);
	const adminOrigin = validateOrigin(configuration.adminOrigin || 'https://admin.hiroic.ai', url.origin);
	const notice = (reason: 'invite' | 'not-found'): RedirectResult => ({
		kind: 'notice',
		reason,
		homeUrl: `${authOrigin}/`,
	});
	const redirect = (path: string, origin = authOrigin): RedirectResult => ({
		kind: 'redirect',
		url: new URL(path, origin).toString(),
	});
	let parts: string[];
	try {
		parts = url.pathname.replace(/\/+$/, '').split('/').slice(1).map(decodeURIComponent);
	} catch {
		return notice('not-found');
	}
	if (
		parts.some(
			(part) =>
				!part ||
				/[/\\]/.test(part) ||
				[...part].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127),
		)
	)
		return notice('not-found');
	const path = `/${parts.join('/')}`;
	const role = (id: string, alignment = false) =>
		uuid.test(id)
			? redirect(`/legacy/roles/${id.toLowerCase()}${alignment ? '?view=alignment' : ''}`)
			: notice('not-found');
	const organization = (id: string, view = '') =>
		uuid.test(id)
			? redirect(`/legacy/organizations/${id.toLowerCase()}${view ? `?view=${view}` : ''}`)
			: notice('not-found');

	if (['/', '/hub', '/role/hub', '/role/hub/archived'].includes(path)) return redirect('/');
	if (path === '/accept-invite') return notice('invite');
	if (path === '/onboarding') return redirect('/onboarding');
	if (path === '/analytics' || path === '/prompts') return redirect('/', adminOrigin);
	if (parts[0] === 'public' && parts[1] === 'chat' && parts.length <= 3) return redirect('/');
	if (path === '/auth/login')
		return redirect(url.searchParams.get('mode') === 'forgot-password' ? '/forgot-password' : '/login');
	if (path === '/auth/register') return redirect('/signup');
	if (path === '/auth/reset-password') return redirect('/forgot-password');
	if (['/auth/verify-email', '/auth/google/callback', '/auth/microsoft/callback'].includes(path))
		return redirect('/login');

	// Literal hub aliases must precede /role/:roleId routes.
	if (parts[0] === 'role' && parts[1] === 'hub' && parts[2]) {
		const suffix = parts.slice(3);
		if (suffix.length <= 1 || suffix.join('/') === 'transcripts/me') return role(parts[2]);
	}
	if (parts[0] === 'hub' && parts[1] && uuid.test(parts[1])) {
		if (parts.length === 2) return organization(parts[1]);
		if (parts.length === 3 && ['archived', 'settings', 'billing'].includes(parts[2])) {
			return organization(parts[1], parts[2] === 'archived' ? 'archived' : 'settings');
		}
		if (parts[2] === 'role' && parts[3]) {
			const suffix = parts.slice(4);
			if (suffix.length <= 1 || suffix.join('/') === 'transcripts/me') return role(parts[3]);
		}
	}
	if (parts[0] === 'role' && parts[1]) {
		if (parts[2] === 'chat') return role(parts[1]);
		if (parts.length === 3 && ['collab', 'invite'].includes(parts[2])) return role(parts[1]);
		if (parts[2] === 'alignment' && (parts[3] === 'chat' || (parts[3] === 'team' && parts.length === 4))) {
			return role(parts[1], true);
		}
	}
	return notice('not-found');
}

export function startLegacyRedirect(configuration: RedirectConfiguration): boolean {
	if (!configuration.authOrigin?.trim()) return false;
	let result: RedirectResult;
	try {
		result = getLegacyRedirect(new URL(window.location.href), configuration)!;
	} catch {
		// A misconfigured cutover must not silently resume writes in the old app.
		window.history.replaceState(null, '', window.location.pathname);
		showNotice('Unable to open the new site. Please contact your administrator.');
		return true;
	}
	if (result.kind === 'redirect') {
		window.location.replace(result.url);
	} else {
		// Remove old invite credentials from the address bar before displaying the notice.
		window.history.replaceState(null, '', window.location.pathname);
		showNotice(
			result.reason === 'invite'
				? 'This invitation belongs to the previous version of Hiroic. Ask the organization or role owner to send you a new invitation.'
				: 'This legacy link is unavailable. Continue to Hiroic to find your organization.',
			result.homeUrl,
		);
	}
	return true;
}

function showNotice(message: string, homeUrl?: string) {
	const root = document.getElementById('root')!;
	const main = document.createElement('main');
	main.style.cssText = 'max-width:36rem;margin:15vh auto;padding:2rem;font:18px/1.6 system-ui,sans-serif';
	const heading = document.createElement('h1');
	heading.textContent = 'Hiroic has moved';
	const text = document.createElement('p');
	text.textContent = message;
	main.append(heading, text);
	if (homeUrl) {
		const link = document.createElement('a');
		link.href = homeUrl;
		link.textContent = 'Go to Hiroic';
		link.rel = 'noreferrer';
		main.append(link);
	}
	root.replaceChildren(main);
}
